import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  NewsletterConsents,
  NewsletterConsentScreen,
  NewsletterConsentState,
  NewsletterConsentUpdateItem,
  useMeNewsletterConsentsQuery,
  useUserNewsletterConsentUpdateMutation,
} from '../../../services';

interface NewsletterConsentChange {
  listKey: string;
  isSubscribed: boolean;
}

export const isSubscribedState = (state: NewsletterConsentState) =>
  state === NewsletterConsentState.SUBSCRIBED || state === NewsletterConsentState.PENDING;

const toConsentState = (isSubscribed: boolean) =>
  isSubscribed ? NewsletterConsentState.SUBSCRIBED : NewsletterConsentState.UNSUBSCRIBED;

export const useNewsletterConsents = (screen: NewsletterConsentScreen) => {
  const { t } = useTranslation();

  const { data: fetchedConsents } = useMeNewsletterConsentsQuery();

  const [updateConsents] = useUserNewsletterConsentUpdateMutation();

  const [localConsents, setLocalConsents] = useState<NewsletterConsents>();

  const consents = localConsents ?? fetchedConsents;

  const saveItems = useCallback(
    async (items: NewsletterConsentUpdateItem[]) => {
      if (!consents || !items.length) {
        return;
      }

      setLocalConsents({
        ...consents,
        lists: consents.lists.map((list) => {
          const item = items.find(({ listKey }) => listKey === list.listKey);
          return item ? { ...list, state: item.state } : list;
        }),
      });

      const revertConsents = () => {
        setLocalConsents(consents);
        toast.error(t('common.error-message'));
      };

      try {
        const updatedConsents = await updateConsents({ items, screen }).unwrap();

        if (!updatedConsents) {
          revertConsents();
          return;
        }

        setLocalConsents(updatedConsents);
      } catch (_) {
        revertConsents();
      }
    },
    [consents, screen, t, updateConsents]
  );

  const setSubscribed = useCallback(
    ({ listKey, isSubscribed }: NewsletterConsentChange) =>
      saveItems([{ listKey, state: toConsentState(isSubscribed) }]),
    [saveItems]
  );

  const setAllSubscribed = useCallback(
    (isSubscribed: boolean) =>
      saveItems(
        (consents?.lists ?? [])
          .filter(({ state }) => isSubscribedState(state) !== isSubscribed)
          .map(({ listKey }) => ({ listKey, state: toConsentState(isSubscribed) }))
      ),
    [consents, saveItems]
  );

  return useMemo(
    () => ({ consents, setSubscribed, setAllSubscribed }),
    [consents, setAllSubscribed, setSubscribed]
  );
};
